// CCPA "Your Privacy Choices" footer link + modal. See snippets/privacy-choices.liquid.
class PrivacyChoices extends HTMLElement {
  static TEST_KEY = 'eef_privacy_test_region';
  static PURPOSES = ['analytics', 'preferences', 'marketing'];

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.inputs = Array.from(this.querySelectorAll('.privacy-choices__switch input'));
    this.savedMessage = this.querySelector('.privacy-choices__saved');

    this.querySelector('.privacy-choices__trigger').addEventListener('click', this.open.bind(this));
    this.querySelector('[data-privacy-close]').addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-privacy-save]').addEventListener('click', this.save.bind(this));
    this.querySelectorAll('[data-privacy-all]').forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.privacyAll === 'true';
        this.inputs.forEach((input) => (input.checked = value));
      });
    });
    // Close when clicking the backdrop.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.withCustomerPrivacy((customerPrivacy) => {
      if (this.isTestMode() || this.isCalifornia(customerPrivacy)) {
        this.hidden = false;
        this.syncShopifyConsent(customerPrivacy);
      }
    });
  }

  isTestMode() {
    const param = new URLSearchParams(window.location.search).get('test');
    try {
      if (param === 'california') sessionStorage.setItem(PrivacyChoices.TEST_KEY, 'california');
      return sessionStorage.getItem(PrivacyChoices.TEST_KEY) === 'california';
    } catch (e) {
      return param === 'california';
    }
  }

  isCalifornia(customerPrivacy) {
    return !!customerPrivacy && typeof customerPrivacy.getRegion === 'function' && customerPrivacy.getRegion() === 'USCA';
  }

  // Loads Shopify's Customer Privacy API; calls back with it, or null if unavailable.
  withCustomerPrivacy(callback) {
    const shopify = window.Shopify;
    if (shopify && shopify.customerPrivacy) return callback(shopify.customerPrivacy);
    if (!shopify || typeof shopify.loadFeatures !== 'function') return callback(null);

    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      callback(error ? null : window.Shopify.customerPrivacy || null);
    });
  }

  // Re-applies saved opt-outs if Shopify's consent cookie was cleared or never set.
  syncShopifyConsent(customerPrivacy) {
    const { choices } = window.eefPrivacy;
    if (!customerPrivacy || PrivacyChoices.PURPOSES.every((purpose) => choices[purpose])) return;
    const current = customerPrivacy.currentVisitorConsent ? customerPrivacy.currentVisitorConsent() : {};
    const mismatch = PrivacyChoices.PURPOSES.some((purpose) => !choices[purpose] && current[purpose] !== 'no');
    if (mismatch) this.setShopifyConsent(customerPrivacy, choices);
  }

  setShopifyConsent(customerPrivacy, choices, done) {
    if (!customerPrivacy || typeof customerPrivacy.setTrackingConsent !== 'function') return done && done();
    customerPrivacy.setTrackingConsent(
      {
        analytics: choices.analytics,
        preferences: choices.preferences,
        marketing: choices.marketing,
        sale_of_data: choices.marketing,
      },
      () => done && done()
    );
  }

  open() {
    const { choices } = window.eefPrivacy;
    this.inputs.forEach((input) => (input.checked = choices[input.name] !== false));
    this.savedMessage.hidden = true;
    this.dialog.showModal();
  }

  save() {
    const previous = window.eefPrivacy.choices;
    const choices = {};
    this.inputs.forEach((input) => (choices[input.name] = input.checked));

    try {
      localStorage.setItem(window.eefPrivacy.storageKey, JSON.stringify(choices));
    } catch (e) {}
    window.eefPrivacy.choices = choices;
    window.eefPrivacy.hasSaved = true;
    window['ga-disable-G-5MEE920GMV'] = !choices.analytics;

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: choices.analytics ? 'granted' : 'denied',
        personalization_storage: choices.preferences ? 'granted' : 'denied',
        ad_storage: choices.marketing ? 'granted' : 'denied',
        ad_user_data: choices.marketing ? 'granted' : 'denied',
        ad_personalization: choices.marketing ? 'granted' : 'denied',
      });
    }

    this.savedMessage.hidden = false;
    // Trackers already running on this page can't be unloaded, so reload when something was turned off.
    const optedOut = PrivacyChoices.PURPOSES.some((purpose) => previous[purpose] && !choices[purpose]);
    this.withCustomerPrivacy((customerPrivacy) => {
      this.setShopifyConsent(customerPrivacy, choices, () => {
        setTimeout(() => {
          this.dialog.close();
          if (optedOut) window.location.reload();
        }, 900);
      });
    });
  }
}

customElements.define('privacy-choices', PrivacyChoices);
